import { useAuth } from "@clerk/clerk-react";
import type { SelfServePlanKey } from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PlanActionModal from "@/components/plan/PlanActionModal";
import PlanSummaryBar from "@/components/plan/PlanSummaryBar";
import PlanTierCards from "@/components/plan/PlanTierCards";
import PlanUpgradeDrawer from "@/components/plan/PlanUpgradeDrawer";
import { PLAN_TAB_STRINGS } from "@/constants/Plans";
import { useCampaignPaymentMethod } from "@/hooks/useCampaignPaymentMethod";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlanActions } from "@/hooks/usePlanActions";
import { usePlans } from "@/hooks/usePlans";
import { useTier } from "@/hooks/useTier";
import type { PlanModalContext } from "@/interfaces/plan";

/** Shown when a plan action fails at the transport level (the drawer renders its own errors). */
const ACTION_ERROR_FALLBACK = "We couldn't update your plan. Please try again.";

const NOTICE_AUTO_HIDE_MS = 5000;

/** GfgModal fires `onClose` before `onPrimary`, so an informational modal needs no action. */
const NO_PRIMARY_ACTION = () => {};

type Notice = { message: string; severity: "info" | "error" };

/** A disabled control swallows pointer events, so the tooltip has to live on a wrapper span. */
function ImpersonationTooltip({ active, children }: { active: boolean; children: ReactElement }) {
  if (!active) return children;
  return (
    <span className="group relative block">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute -top-2 left-1/2 z-50 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-graphite px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {PLAN_TAB_STRINGS.impersonationTooltip}
      </span>
    </span>
  );
}

/**
 * Account > Plan — the tab body.
 *
 * Action routing:
 * - `Switch to Pro` / `Switch to Pro Plus`            → the drawer, for that plan.
 * - `Cancel subscription` / `Downgrade` / toggle OFF  → Enterprise modal on Enterprise, else confirm modal.
 * - `Keep Good Pro` / `Cancel downgrade` / toggle ON  → PATCH auto-renew straight away, no confirmation.
 *
 * Enterprise sees the summary bar only. None of the cards is something it can act on — its plan
 * changes are contract-run — so the chooser is not shown at all.
 */
export default function PlanPanel() {
  // Billing — renewal date and scheduled cancellation — is what /tier is for and what this tab
  // is about. The PLAN itself is an entitlement, so it is read from the matrix.
  const { ent } = useMyEntitlements();
  const { renewalDate, cancelAtPeriodEnd, billing, isLoading } = useTier();
  const plan = ent?.plan.key;
  const { upgrade, cancel, setAutoRenew } = usePlanActions();
  const { can } = usePermissions();
  const { actor } = useAuth();
  const { byKey } = usePlans();
  const planName = plan ? (byKey(plan)?.display.name ?? "") : "";
  // Warms the payment-method query so the drawer's card section isn't loading on first open.
  useCampaignPaymentMethod();

  /** The plan checkout is open for; null closes the drawer. */
  const [checkoutPlan, setCheckoutPlan] = useState<SelfServePlanKey | null>(null);
  /** Which account modal is up; null closes them all. */
  const [planModal, setPlanModal] = useState<PlanModalContext | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    if (!notice) return;
    const id = setTimeout(() => setNotice(null), NOTICE_AUTO_HIDE_MS);
    return () => clearTimeout(id);
  }, [notice]);

  const isImpersonating = !!actor;
  const scheduled = cancelAtPeriodEnd === true;
  const isEnterprise = plan === "enterprise";
  // Money paths fail closed: an impersonated session can look at the tab but never pay,
  // a caller without TIER edit never reaches it, and an in-flight action can't be fired twice.
  const actionsDisabled =
    isImpersonating ||
    !can("edit", "TIER") ||
    upgrade.isPending ||
    cancel.isPending ||
    setAutoRenew.isPending;

  /** Starter → Pro or Pro Plus. On Enterprise every plan change is contract-run instead. */
  function handleSelectPlan(target: SelfServePlanKey) {
    if (actionsDisabled) return;
    if (isEnterprise) {
      setPlanModal("enterprise");
      return;
    }
    setCheckoutPlan(target);
  }

  /**
   * `Cancel subscription`, the Starter card's `Downgrade` and toggling auto-renew OFF are the
   * same flow with the same confirmation — the mutation waits for the confirm.
   */
  function handleDowngrade() {
    if (actionsDisabled) return;
    setPlanModal(isEnterprise ? "enterprise" : "downgrade");
  }

  /**
   * Restores auto-renewal. Reached from the scheduled banner's `Keep Good Pro`, the Starter
   * card's `Cancel downgrade` and the switch moving back ON — all three mutate directly since
   * there is nothing destructive to confirm.
   */
  async function resumeAutoRenew() {
    if (actionsDisabled) return;
    try {
      const result = await setAutoRenew.mutateAsync(true);
      if (result.status === "error") setNotice({ message: result.message, severity: "error" });
    } catch {
      setNotice({ message: ACTION_ERROR_FALLBACK, severity: "error" });
    }
  }

  /** The switch never mutates on its way OFF — that opens the confirmation instead. */
  function handleToggleAutoRenew(autoRenew: boolean) {
    if (autoRenew) {
      void resumeAutoRenew();
      return;
    }
    handleDowngrade();
  }

  async function handleConfirmDowngrade() {
    if (actionsDisabled) return;
    try {
      const result = await cancel.mutateAsync();
      if (result.status === "error") {
        setNotice({ message: result.message, severity: "error" });
        return;
      }
      // Already closed by the modal's onClose (it fires first); idempotent either way.
      setPlanModal(null);
    } catch {
      setNotice({ message: ACTION_ERROR_FALLBACK, severity: "error" });
    }
  }

  /** Enterprise has no self-service path — answer with a toast. */
  function handleTalkToSales() {
    setNotice({ message: PLAN_TAB_STRINGS.talkToSalesToast, severity: "info" });
  }

  // Wait for the plan as well as the billing dates. This tab's whole subject is which plan the
  // organization is on, so rendering it before that is known is worse than a moment of skeleton.
  if (isLoading || !plan) {
    return (
      <div className="flex justify-center py-[48px]">
        <svg
          className="h-10 w-10 animate-spin text-dark-dust"
          viewBox="0 0 24 24"
          fill="none"
          role="progressbar"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
    );
  }

  return (
    // Prototype `.cs-wrap` (client-experience-prototype.html:644) caps the whole Account screen
    // at 1120px, left-aligned after the sidebar — no auto margins. `.cs-plan-current` even sets
    // `max-width: none` so the summary bar fills that width instead of `.pf-card`'s 680px. So the
    // cap belongs here, on the tab, not on the grid: capping only the grid leaves the summary bar
    // running to the viewport edge, and centring it puts the two on different left edges.
    <div className="max-w-[1120px]">
      <ImpersonationTooltip active={isImpersonating}>
        <PlanSummaryBar
          plan={plan}
          billing={billing}
          renewalDate={renewalDate}
          cancelAtPeriodEnd={cancelAtPeriodEnd}
          onCancel={handleDowngrade}
          onToggleAutoRenew={handleToggleAutoRenew}
          onKeepPlan={() => void resumeAutoRenew()}
          disabled={actionsDisabled}
        />
      </ImpersonationTooltip>

      {/* The current card's "Your plan" badge is absolutely positioned above the card, so the
          grid needs top clearance or the badge clips into the summary bar. */}
      {!isEnterprise && (
        <div className="mt-10">
          <ImpersonationTooltip active={isImpersonating}>
            <PlanTierCards
              plan={plan}
              pendingDowngrade={scheduled}
              onSelectPlan={handleSelectPlan}
              onDowngrade={handleDowngrade}
              onCancelDowngrade={() => void resumeAutoRenew()}
              onTalkToSales={handleTalkToSales}
              disabled={actionsDisabled}
            />
          </ImpersonationTooltip>
        </div>
      )}

      <PlanUpgradeDrawer
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
        disabled={actionsDisabled}
      />
      <PlanActionModal
        context={planModal}
        onClose={() => setPlanModal(null)}
        onPrimary={
          planModal === "downgrade" ? () => void handleConfirmDowngrade() : NO_PRIMARY_ACTION
        }
        renewalDate={renewalDate}
        planName={planName}
      />

      {notice &&
        createPortal(
          <div
            role={notice.severity === "error" ? "alert" : "status"}
            className={`fixed bottom-6 left-1/2 z-[1400] flex -translate-x-1/2 items-center gap-3 rounded-md px-4 py-3 font-lato text-sm font-medium text-white shadow-lg ${
              notice.severity === "error" ? "bg-good-wine" : "bg-good-night"
            }`}
          >
            <span>{notice.message}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Close"
              title="Close"
              className="inline-flex shrink-0 cursor-pointer items-center border-none bg-transparent p-0 text-white/80 transition-colors hover:text-white"
            >
              <CloseIcon className="text-[18px]" />
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
